import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DoubleArrow from '@material-ui/icons/DoubleArrow';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import get from 'lodash/get';
import OptionTiles from '../OptionTiles/OptionTiles';
import { green, offwhite } from '../../Constants/Colors';
import { useDispatch, useSelector } from 'react-redux';
import { setVisible } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import {
  getActiveStepComponents,
  getNextStepComponents,
  getConfiguration,
  getOptionClassificationOptions,
  getCheckedComponents,
} from '../../Reducers/Configurator/Selectors';
import { loadStepOptionClassificationOptions, selectOption } from '../../Reducers/Configurator/Actions';
import ComponentHint from '../ComponentHint/ComponentHint';

const useStyles = makeStyles(
  (theme) => ({
    partsContainer: {
      [theme.breakpoints.down('sm')]: {
        padding: '0px 20px',
      },
    },
    optionClassificationTitle: {
      padding: '20px 0px',
    },
    descriptionContainer: {
      maxWidth: '900px',
    },
    optionClassificationDescription: {
      paddingBottom: 40,
      fontSize: 17,
      fontFamily: 'Inter',
      fontWeight: 300,
    },
    animation: {
      display: 'flex',
      alignItems: 'center',
      '&:before': {
        fontSize: '1.5em',
      },
    },
    productPartDataContainer: {
      position: 'relative',
      '& .loadingoverlay': {
        backgroundColor: offwhite,
        opacity: 0.8,
      },
    },
    container: {
      top: -50,
      left: '50%',
      transform: 'translateX(-50%)',
      flexDirection: 'column-reverse',
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    arrowIcon: {
      transform: 'rotate(90deg)',
      color: green,
    },
    hintText: {
      textTransform: 'uppercase',
      fontWeight: 'bold',
      maxWidth: 150,
      textAlign: 'center',
    },
  }),
  {
    name: 'ProductPartsData',
  }
);

const ProductPartsData = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const stepOptionClassifications = useSelector(getActiveStepComponents);
  const nextStepOptionClassifications = useSelector(getNextStepComponents);
  const configuration = useSelector(getConfiguration);
  const stepOptionClassificationInfo = useSelector(getOptionClassificationOptions);
  const { isFetching } = useSelector((state) => state.api);
  const checkedComponents = useSelector(getCheckedComponents);
  const optionClassifications = configuration.optionclassifications;

  useEffect(() => {
    dispatch(setVisible('startHere', true));
  }, [dispatch]);

  useEffect(() => {
    // loading options of Active Step OptionClassifications
    if (stepOptionClassifications && stepOptionClassifications.length > 0) {
      dispatch(loadStepOptionClassificationOptions(stepOptionClassifications));
    }
  }, [stepOptionClassifications, configuration, optionClassifications, dispatch]);

  useEffect(() => {
    // preload options of next step OptionClassifications
    if (nextStepOptionClassifications?.length > 0) {
      dispatch(loadStepOptionClassificationOptions(nextStepOptionClassifications));
    }
  }, [nextStepOptionClassifications, configuration, optionClassifications, dispatch]);

  const getHint = (optionclassification) => {
    const attributes = get(optionclassification, 'attributes', []);
    const hintAttribute = attributes.find((attr) => attr.identifier === 'component_hint_text');
    return (hintAttribute?.values || [])[0]?.translated_value;
  };

  const lastCheckedIndex = stepOptionClassifications.findLastIndex((identifier) =>
    checkedComponents.includes(identifier)
  );
  const jumpToComponentIdentifier =
    lastCheckedIndex === -1
      ? stepOptionClassifications?.[0]
      : stepOptionClassifications[Math.min(lastCheckedIndex + 1, stepOptionClassifications.length - 1)];

  return (
    <div className={classes.productPartDataContainer}>
      <a className={classes.container} href={`#${jumpToComponentIdentifier}`}>
        <div className={classes.animation}>
          <DoubleArrow className={classes.arrowIcon} />
        </div>
        <Typography className={classes.hintText} variant="body2">
          {t(!checkedComponents.length ? 'productPart.startHere' : 'productPart.configureFurther')}
        </Typography>
      </a>
      <div className={classes.partsContainer}>
        <LoadingOverlay show={isFetching} />
        {stepOptionClassifications.map((oc) => {
          const optionclassification = stepOptionClassificationInfo.find((o) => o.identifier === oc);
          const hint = getHint(optionclassification);
          const selectedoptions = optionclassification
            ? get(
                configuration.optionclassifications.find(
                  (oc) => oc.identifier === optionclassification.identifier
                ),
                'selectedoptions',
                []
              ) || [] // lodash `get`'s default only applies to `undefined`, not the API's explicit `null`
            : [];
          const isSingleAutoSelected =
            optionclassification?.options?.length === 1 &&
            selectedoptions.length === 1 &&
            selectedoptions[0]?.identifier === optionclassification.options[0]?.identifier;
          return (
            optionclassification && !isSingleAutoSelected && (
              <div key={optionclassification.identifier} id={optionclassification.identifier}>
                <Typography variant="h2" className={classes.optionClassificationTitle}>
                  {optionclassification.title}
                </Typography>
                <div className={classes.descriptionContainer}>
                  <Typography variant="body2" className={classes.optionClassificationDescription}>
                    {optionclassification.description}
                  </Typography>
                  {hint && <ComponentHint hintText={hint} />}
                </div>
                <OptionTiles
                  optionclassification={optionclassification}
                  options={optionclassification.options}
                  selectedoptions={selectedoptions || []}
                  selectOption={(option) => dispatch(selectOption(option, optionclassification, true))}
                />
              </div>
            )
          );
        })}
      </div>
    </div>
  );
};

export default ProductPartsData;
